import { useQueryClient } from '@tanstack/react-query';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { warehouseApi } from 'src/api/warehouse';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { WarehouseDemandRequestResponseDto } from 'woofwoof-api';
import {
  DemandProductOption,
  toDemandProductOption,
} from '../components/demand/WarehouseDemandRequestForm.schema';
import WarehouseDemandRequestFormModal from '../components/demand/WarehouseDemandRequestFormModal';
import { WarehouseErrorCode, warehouseErrorCode } from '../utils/errors';
import { warehouseRequestOptions } from '../utils/request';
import {
  pinnedDemandKey,
  useDemandOperationKey,
} from './useDemandOperationKey';
import {
  CreateDemandBatchVariables,
  useWarehouseDemandMutations,
} from './useWarehouseDemandMutations';
import { demandProductLookupKey } from './warehouseDemandQueryKeys';

export interface DemandRequestModalControls {
  /**
   * Call it from an arrow (`() => demand.open()`) or with a `{ id, name }`
   * object, never as a raw event handler.
   */
  open: (product?: DemandProductOption) => void;
  /** Phase 2's shortage warning knows only the Product id. */
  openForProductId: (productId: string) => void;
  /** #690: the medicine details panel's request for a registry medicine by name. */
  openForName: (name: string) => void;
  /** Render it once in the host; `null` without a selected clinic. */
  modal: ReactNode;
}

interface ModalState {
  isOpen: boolean;
  product: DemandProductOption | null;
  /** A request for another product: its name ("Inny produkt"). */
  name: string | null;
  /** The clinic the form was opened for. */
  organizationId: string | undefined;
}

const CLOSED: ModalState = {
  isOpen: false,
  product: null,
  name: null,
  organizationId: undefined,
};

/** A refused attempt and the key it sent, so a late answer never labels another series. */
interface DemandFailure {
  key: string;
  code: WarehouseErrorCode | undefined;
}

/**
 * "Zgłoś zapotrzebowanie" from any entry point: Produkty, the demand tab, a
 * medication shortage. The launcher holds the clinic's operation-key series
 * and the batch create mutation (D-3), like the Sale's entry point, so the form
 * settles each attempt with the key it sent.
 */
export function useDemandRequestModal(): DemandRequestModalControls {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganizationContext();
  const organizationId = selectedOrganization?.id;
  const [state, setState] = useState<ModalState>(CLOSED);
  // D-3: one series per clinic, named by every entry point; an unresolved
  // one outlives this host and is resent from wherever the form opens next.
  const operationKeys = useDemandOperationKey(organizationId);
  const { createBatch } = useWarehouseDemandMutations();
  // F-8: the series' last refusal. The key stays held after 422
  // WAREHOUSE_DISABLED, but that attempt saved nothing.
  const [failure, setFailure] = useState<DemandFailure | null>(null);
  const blocked =
    operationKeys.pending &&
    failure?.code === 'WAREHOUSE_DISABLED' &&
    failure.key === pinnedDemandKey(operationKeys);
  // Only the latest open decides; a slower name lookup never opens later.
  // Closing and a clinic switch count too, so neither is undone by a lookup.
  const latestOpen = useRef(0);

  // Another clinic closes the form for good: selecting the first clinic again
  // never brings back its form or a pending name lookup.
  useEffect(() => {
    latestOpen.current += 1;
    setState(CLOSED);
  }, [organizationId]);

  const show = useCallback(
    (
      product: DemandProductOption | null,
      forOrganization: string | undefined,
      name: string | null = null,
    ): void => {
      setState((current) =>
        // Opening again while open keeps the form and what the user typed.
        current.isOpen && current.organizationId === forOrganization
          ? current
          : { isOpen: true, product, name, organizationId: forOrganization },
      );
    },
    [],
  );

  const open = useCallback(
    (product?: DemandProductOption): void => {
      latestOpen.current += 1;
      show(
        product
          ? {
              id: product.id,
              name: product.name,
              ...(product.stock && { stock: product.stock }),
            }
          : null,
        organizationId,
      );
    },
    [organizationId, show],
  );

  const openForProductId = useCallback(
    (productId: string): void => {
      if (!organizationId) {
        open();
        return;
      }
      latestOpen.current += 1;
      const attempt = latestOpen.current;
      // The master detail key and payload, so the cache is shared with
      // Phase 1's useWarehouseProduct and scoped to this clinic (S3).
      void queryClient
        .fetchQuery({
          queryKey: demandProductLookupKey(organizationId, productId),
          queryFn: async ({ signal }) =>
            (
              await warehouseApi.warehouseProductControllerGetProduct(
                { id: productId },
                warehouseRequestOptions(organizationId, signal),
              )
            ).data,
        })
        .then(
          (product): DemandProductOption | null =>
            toDemandProductOption(product),
          () => null,
        )
        .then((product) => {
          if (attempt === latestOpen.current) {
            show(product, organizationId);
          }
        });
    },
    [open, organizationId, queryClient, show],
  );

  const openForName = useCallback(
    (name: string): void => {
      latestOpen.current += 1;
      show(null, organizationId, name.trim() || null);
    },
    [organizationId, show],
  );

  const close = useCallback((): void => {
    latestOpen.current += 1;
    setState((current) => ({ ...current, isOpen: false }));
  }, []);

  // Only the series' own answers change its label: a late answer of an ended
  // series is ignored, as the registry ignores its settle.
  const submit = async (
    variables: CreateDemandBatchVariables,
  ): Promise<WarehouseDemandRequestResponseDto[]> => {
    const key = variables.dto.operationKey;
    try {
      const requests = await createBatch.mutateAsync(variables);
      setFailure((last) => (last?.key === key ? null : last));
      // Saved: close the form of the clinic it was sent for, never a form
      // opened for another clinic meanwhile.
      setState((current) =>
        current.organizationId === variables.organizationId
          ? { ...current, isOpen: false }
          : current,
      );
      return requests;
    } catch (error) {
      if (key === pinnedDemandKey(operationKeys)) {
        setFailure({ key, code: warehouseErrorCode(error) });
      }
      throw error;
    }
  };

  return {
    open,
    openForProductId,
    openForName,
    modal: organizationId ? (
      <WarehouseDemandRequestFormModal
        // A form opened for another clinic is closed (S3).
        isOpen={state.isOpen && state.organizationId === organizationId}
        initialProduct={state.product}
        initialName={state.name}
        organizationId={organizationId}
        isSubmitting={createBatch.isLoading}
        operationKeys={operationKeys}
        blocked={blocked}
        onClose={close}
        onSubmit={submit}
      />
    ) : null,
  };
}
